import { token } from '@/infrastructure/container';
import type { ILogger } from './Logger';

// Handing a file the app built to the browser, wrapped. A store may not touch `document` or `URL`
// directly (INVARIANTS rule 6's platform ban), and the recipe is rule 12's: interface, token, plain
// class, fake in src/testing/fakes.ts.
//
// One thing goes through here today: the team-selection CSV the admin downloads days after the
// grading day (docs/reporting.md). There is no server round trip and there is no upload — the file
// is built on the laptop from the folded session and never leaves it.
//
// No `start()`: like DomStorage this is a synchronous accessor with no event source, so there is
// nothing to arm and nothing to publish.

export interface IDomDownload {
  // Offers `contents` to the browser as a file called `filename`. Returns nothing: a browser gives
  // no signal that a download completed, or that the user kept it, so a boolean here would be a
  // lie. A platform refusal is logged and swallowed, exactly as DomStorage does — a laptop that
  // cannot open a save dialog must not take the admin screen down with it.
  save(filename: string, contents: string, mimeType: string): void;
}

export const TOKEN_IDomDownload = token<IDomDownload>('IDomDownload');

export const CSV_MIME = 'text/csv;charset=utf-8';

// Blob + object URL + a synthetic anchor click, which is the whole of how a browser saves a file a
// page generated.
export class AnchorDomDownload implements IDomDownload {
  constructor(private readonly logger: ILogger) {}

  save(filename: string, contents: string, mimeType: string = CSV_MIME): void {
    let url: string | undefined;
    try {
      url = URL.createObjectURL(new Blob([contents], { type: mimeType }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      // In the document, because a synthetic click on a detached anchor is ignored by some
      // browsers, and removed again immediately because nothing on the admin screen should grow an
      // invisible link per export.
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (cause) {
      this.logger.warn('download failed', filename, cause);
    } finally {
      // On the next macrotask, not in this one: the click starts a fetch of the object URL and a
      // URL revoked in the same turn can cancel it. A leaked blob is a page reload; a cancelled
      // download is the admin's report.
      if (url !== undefined) revokeSoon(url);
    }
  }
}

function revokeSoon(url: string): void {
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
